// Keys are stored data and must never be renamed.
// This file is the only source of tab keys; the API stores whatever it is given.
// Builders take the component instance because some labels are runtime values.
export function buildClientNav(vm) {
  const hcaiEnabled = !!(
    ((vm.$store && vm.$store.state.settings.publicSettings) || {}).data || {}
  ).HCAI_ENABLED;
  return [
    {
      key: "profile",
      label: "Profile",
      to: "patients.dashboard",
      icon: "home",
      subLinks: [
        "patients.dashboard",
        "patients.basic",
        "patients.classes",
        "patients.address",
        "patients.phones",
        "patients.emails",
        "patients.services",
        "patients.creditCards",
        "patients.transactions",
      ],
    },
    {
      key: "healthcare",
      label: "Healthcare",
      to: "patients.medical",
      icon: "notes-medical",
    },
    {
      key: "providers",
      label: vm.$providerLabel({ type: "plural", capitalize: true }),
      to: "patients.providers",
      icon: "user-injured",
      hideForAdmin: vm.$isAdmin() && !vm.$can("providers", "view"),
    },
    {
      key: "contacts",
      label: "Contacts",
      to: "patients.contacts",
      icon: "file",
    },
    {
      key: "documents",
      label: "Documents",
      to: "patients.documents",
      icon: "folder-open",
      hideForPatient: false,
      hideForProvider: false,
    },
    {
      key: "timeline",
      label: vm.$customerLabel
        ? `${vm.$customerLabel({
            type: "singular",
            capitalize: true,
          })} Timeline`
        : "Timeline",
      to: "patients.timeline",
      icon: "file",
      hideForPatient: true,
    },
    {
      key: "notes",
      label: "Forms",
      to: "patients.notes",
      icon: "clipboard",
      hideForPatient: true,
    },
    {
      key: "intake_forms",
      label: "Intake Forms",
      to: "patients.intakeForms",
      icon: "file-medical",
    },
    {
      key: "invoices",
      label: "Invoices",
      to: "patients.invoices",
      icon: "file-invoice-dollar",
      hideForPatient: true,
      hideForProvider: true,
    },
    {
      key: "budget_admin",
      label: "Budget",
      editorLabel: "Budget (Admin view)",
      to: "patients.budget",
      icon: "money-check-alt",
      hideForPatient: true,
      hideForProvider: true,
      badgeKey: "budgets",
      subLinks: ["patients.budget", "patients.budget-charts"],
    },
    {
      key: "budget_provider",
      label: "Budget",
      editorLabel: "Budget (Provider view)",
      to: "patients.budget.chart",
      icon: "file-invoice-dollar",
      hideForPatient: true,
      hideForAdmin: true,
      subLinks: ["patients.budget", "patients.budget-charts"],
    },
    {
      key: "shared_recordings",
      label: "Shared Recordings",
      to: "patients.shared-recordings",
      icon: "camera",
    },
    {
      key: "availability",
      label: "Availability",
      to: "patients.availability",
      icon: "user-clock",
    },
    {
      key: "events",
      label: "Events",
      action: "openCalendarModal",
      icon: "calendar",
    },
    {
      key: "integration",
      label: "Integration",
      to: "client.c3.integration",
      icon: "anchor",
      onlyPatient: true,
    },
    {
      key: "symptoms_diary",
      label: "Symptoms Diary",
      to: "patients.symptomsDiary",
      icon: "book-medical",
      subLinks: ["patients.symptomsDiary", "patients.symptomsDiaryReports"],
    },
    {
      key: "symptoms_tracker",
      label: "Symptoms Tracker",
      to: "patients.symptoms",
      icon: "chart-line",
      hideForPatient: true,
      subLinks: ["patients.symptoms", "patients.reports"],
    },
    {
      key: "engagement_notes",
      label: "Engagement Notes",
      to: "patients.engagement-notes",
      icon: "notes-medical",
      hideForPatient: true,
    },
    ...(hcaiEnabled
      ? [
          {
            key: "hcai",
            label: "HCAI Info",
            to: "patients.hcai",
            icon: "file-invoice",
            hideForPatient: true,
            hideForProvider: true,
          },
        ]
      : []),
  ];
}

// The asymmetry is intentional: the provider branch ignores `hideForAdmin`, and the
// patient branch ignores `onlyPatient` and `hideForAdmin`.
export function filterClientNav(items, user) {
  if (user && user.isProvider) {
    return items.filter((item) => !item.hideForProvider && !item.onlyPatient);
  } else if (user && user.isPatient) {
    return items.filter((item) => !item.hideForPatient);
  } else {
    return items.filter((item) => !item.onlyPatient && !item.hideForAdmin);
  }
}

// These keys are the allow-list values views/providers/profile/Index.vue passes
// as `itemKeysToDisplay`.
export function buildProviderNav(vm) {
  return [
    {
      key: "profile",
      label: "Profile",
      to: "providers.basic",
      icon: "home",
      subLinks: [
        "providers.basic",
        "providers.classes",
        "providers.clinics",
        "providers.address",
        "providers.phones",
        "providers.emails",
      ],
    },
    {
      key: "profession",
      label: "Professions",
      to: "providers.profession",
      icon: "user-md",
    },
    {
      key: "payroll",
      label: "Pay Details",
      to: "providers.payroll",
      icon: "money-check",
      permissions: [
        { isProvider: true, key: "payDetails", expectedPermission: "view" },
        { isAdmin: true, key: "payrollReport", expectedPermission: "view" },
      ],
    },
    {
      key: "patients",
      label: vm.$customerLabel({ type: "plural", capitalize: true }),
      to: "providers.patients",
      icon: "user-injured",
    },
    {
      key: "contacts",
      label: "Contacts",
      to: "providers.contacts",
      icon: "file",
      permissions: [
        { isProvider: true, key: "contact", expectedPermission: "view" },
        { isAdmin: true, key: "providers", expectedPermission: "view" },
      ],
    },
    {
      key: "documents",
      label: "Documents",
      to: "providers.documents",
      icon: "folder-open",
      permissions: [
        { isProvider: true, key: "document", expectedPermission: "view" },
        { isAdmin: true, key: "providers", expectedPermission: "view" },
      ],
    },
    {
      key: "timeline",
      label: `${vm.$providerLabel({
        type: "singular",
        capitalize: true,
      })} Timeline`,
      to: "providers.timeline",
      icon: "file",
    },
    {
      key: "shared-recordings",
      label: "Shared Recordings",
      to: "providers.shared-recordings",
      icon: "camera",
    },
    {
      key: "availability",
      label: "Availability",
      to: "providers.availability",
      icon: "user-clock",
    },
    {
      key: "events",
      label: "Events",
      action: "openCalendarModal",
      icon: "calendar",
    },
    {
      key: "integration",
      label: "Integration",
      to: "provider.c3.integration",
      icon: "anchor",
      providerOnly: true,
    },
  ];
}

export function filterProviderNav(items, vm, itemKeysToDisplay) {
  return items.filter((item) => {
    if (itemKeysToDisplay && !itemKeysToDisplay.includes(item.key)) {
      return false;
    }
    if (item.providerOnly && !(vm.user && vm.user.isProvider)) {
      return false;
    }
    if (item.permissions && Array.isArray(item.permissions)) {
      return item.permissions.some((perm) => {
        if (perm.isProvider && vm.$isProvider()) {
          return vm.$can(perm.key, perm.expectedPermission);
        }
        if (perm.isAdmin && vm.$isAdmin()) {
          return vm.$can(perm.key, perm.expectedPermission);
        }
        return false;
      });
    }
    return true;
  });
}

export function buildAdminProfileNav() {
  return [
    {
      key: "profile",
      label: "Profile",
      to: "admin.profile.basic",
      icon: "home",
      subLinks: [
        "admin.profile.basic",
        "admin.profile.address",
        "admin.profile.phones",
      ],
    },
    {
      key: "documents",
      label: "Documents",
      to: "admin.profile.documents",
      query: {
        src: "admin",
      },
      icon: "folder-open",
    },
    {
      key: "timeline",
      label: "Timeline",
      to: "admin.profile.timeline",
      icon: "file",
    },
    {
      key: "payroll",
      label: "Pay Details",
      to: "admin.profile.payroll",
      icon: "money-check",
    },
    {
      key: "professions",
      label: "Professions",
      to: "admin.profile.professions",
      icon: "user-injured",
    },
    {
      key: "shared_recordings",
      label: "Shared Recordings",
      to: "admin.profile.shared-recordings",
      icon: "camera",
    },
    {
      key: "availability",
      label: "Availability",
      to: "admin.profile.availability",
      icon: "user-clock",
    },
    {
      key: "events",
      label: "Events",
      action: "openCalendarModal",
      icon: "calendar",
    },
    {
      key: "integration",
      label: "Integration",
      to: "admin.c3.integration",
      icon: "anchor",
    },
  ];
}

export const MANDATORY_BAR_KEYS = ["profile"];

export function profileNavEditorLabel(item) {
  return (item && (item.editorLabel || item.label)) || "";
}

export function isProfileNavItemActive(item, routeName) {
  return (
    item.to === routeName ||
    (Array.isArray(item.subLinks) && item.subLinks.includes(routeName))
  );
}
